import { extractActionTextValue } from '@/services/action/normalizeActionTransfer'

export function normalizeInlineActionFormat(rawFormat: unknown): { type: string; name?: string } {
  const unwrap = (value: unknown): string => {
    if (!value) return ''
    if (typeof value === 'string') return value.trim()
    if (typeof value === 'object') {
      const obj = value as Record<string, unknown>
      if (typeof obj.type === 'string') {
        const type = obj.type.trim()
        const name = typeof obj.name === 'string' ? obj.name.trim() : ''
        return name ? `${type}=${name}` : type
      }
      if (typeof obj.value === 'string') return obj.value.trim()
      if (typeof obj._text === 'string') return obj._text.trim()
      if (typeof obj.name === 'string') return obj.name.trim()
    }
    return String(value).trim()
  }

  const normalized = unwrap(rawFormat)
  if (!normalized) {
    return { type: '' }
  }

  const separatorIndex = normalized.indexOf('=')
  if (separatorIndex < 0) {
    return { type: normalized.toUpperCase() }
  }

  const type = normalized.slice(0, separatorIndex).trim().toUpperCase()
  const name = normalized.slice(separatorIndex + 1).trim()
  return { type, name }
}

export function unwrapInlineActionCellValue(value: unknown): unknown {
  if (value === null || value === undefined) return value
  if (typeof value === 'object' && value !== null && 'value' in (value as object)) {
    return (value as { value: unknown }).value
  }
  return value
}

export function resolveInlineActionDefaultTitle(action: { title?: unknown }): string {
  return String(extractActionTextValue(action.title) ?? '').trim()
}

export function resolveInlineActionTitle(
  format: { type: string },
  action: { title?: unknown },
  cellValue: unknown,
): string {
  if (format.type === 'SPECIFIEDNAMED' || format.type === 'SPECIFIEDTITLED') {
    if (cellValue === null || cellValue === undefined) {
      return ''
    }
    const title = String(cellValue).trim()
    return title || resolveInlineActionDefaultTitle(action)
  }

  return resolveInlineActionDefaultTitle(action)
}

export function resolveInlineActionCellConfig(
  inlineActions: Array<{ name?: unknown; title?: unknown }>,
  column: { name?: string; format?: unknown },
  row: unknown,
  getFieldValueFromRow: (row: unknown, fieldName: string) => unknown,
): { action: (typeof inlineActions)[number]; title: string } | null {
  const format = normalizeInlineActionFormat(column?.format)
  const cellValue = unwrapInlineActionCellValue(
    getFieldValueFromRow(row, String(column?.name ?? '')),
  )

  for (const action of inlineActions) {
    const actionName = String(extractActionTextValue(action.name) ?? '').trim()

    if (format.type === 'FIRST') {
      return {
        action,
        title: resolveInlineActionTitle(format, action, cellValue),
      }
    }

    if (format.type === 'NAMED' && cellValue != null && String(cellValue) === actionName) {
      return {
        action,
        title: resolveInlineActionTitle(format, action, cellValue),
      }
    }

    if (
      (format.type === 'SPECIFIEDNAMED' || format.type === 'SPECIFIEDTITLED') &&
      format.name &&
      actionName === format.name
    ) {
      const title = resolveInlineActionTitle(format, action, cellValue)
      if (!title) {
        return null
      }

      return { action, title }
    }

    if (format.type === 'SPECIFIED' && format.name && actionName === format.name) {
      return {
        action,
        title: resolveInlineActionTitle(format, action, cellValue),
      }
    }
  }

  return null
}
